import { useTranslation } from 'react-i18next';

import type { QmsBranding, QmsToken } from './types';

interface Props {
  token: QmsToken;
  serviceName: string;
  serviceNameAr?: string | null;
  branding: QmsBranding | null;
  locale: string;
}

function formatTime(iso: string, locale: string): string {
  try {
    return new Date(iso).toLocaleString(locale, {
      hour: '2-digit',
      minute: '2-digit',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

/** Token receipt sized for an 80mm thermal printer. Rendered inside a
 *  `.qms-print-host` container; `index.css` reveals only this subtree
 *  while `body.qms-print-armed` is set (see {@link printReceipt}). */
export function QmsTokenReceipt({
  token,
  serviceName,
  serviceNameAr,
  branding,
  locale,
}: Props): JSX.Element {
  const { t } = useTranslation('qms');
  return (
    <div className="qms-print-receipt">
      {branding?.logo_url ? (
        <img src={branding.logo_url} alt="" className="qms-print-logo" />
      ) : null}
      {branding?.header_text ? (
        <div className="qms-print-header">{branding.header_text}</div>
      ) : null}
      <div className="qms-print-service">{serviceName}</div>
      {serviceNameAr ? (
        <div className="qms-print-service" dir="rtl" lang="ar">
          {serviceNameAr}
        </div>
      ) : null}
      <div className="qms-print-code">{token.display_code}</div>
      <div className="qms-print-meta">
        {t('print.issued_at')}: {formatTime(token.issued_at, locale)}
      </div>
      {token.customer_name ? (
        <div className="qms-print-meta">{token.customer_name}</div>
      ) : null}
      {branding?.footer_text ? (
        <pre className="qms-print-footer">{branding.footer_text}</pre>
      ) : null}
    </div>
  );
}
